import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, Download, Wallet, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { listPayments, listInvoices, recordPayment } from "@/lib/accounting/accounting.functions";
import { downloadCSV, fmtDate, fmtMoney, toCSV } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/admin/accounting/deposits")({
  component: Deposits,
});

const RANGES = [
  { id: "all", label: "All time" },
  { id: "ytd", label: "YTD" },
  { id: "month", label: "This month" },
  { id: "lastmonth", label: "Last month" },
  { id: "week", label: "This week" },
] as const;

function startOf(id: string): Date | null {
  const now = new Date();
  if (id === "all") return null;
  if (id === "ytd") return new Date(now.getFullYear(), 0, 1);
  if (id === "month") return new Date(now.getFullYear(), now.getMonth(), 1);
  if (id === "lastmonth") return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  if (id === "week") {
    const w = new Date(now); w.setDate(now.getDate() - now.getDay()); w.setHours(0, 0, 0, 0); return w;
  }
  return null;
}
function endOf(id: string): Date | null {
  if (id !== "lastmonth") return null;
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
}

function Deposits() {
  const payFn = useServerFn(listPayments);
  const invFn = useServerFn(listInvoices);
  const recordFn = useServerFn(recordPayment);
  const qc = useQueryClient();
  const { data: payments, isLoading: loadingPay } = useQuery({ queryKey: ["accounting", "payments"], queryFn: () => payFn() });
  const { data: invoices, isLoading: loadingInv } = useQuery({ queryKey: ["accounting", "invoices"], queryFn: () => invFn() });

  const [rangeId, setRangeId] = useState<string>("ytd");
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [form, setForm] = useState({ amount: 0, method: "check" as "cash" | "check" | "ach" | "zelle" | "venmo" | "credit_card_external" | "quickbooks" | "other", reference: "" });

  const openMark = (inv: any) => {
    setMarkingId(inv.id);
    setForm({ amount: Number(inv.balance_due || 0), method: "check", reference: "" });
  };
  const saveMark = async () => {
    if (!markingId || !form.amount) { toast.error("Amount required"); return; }
    try {
      await recordFn({ data: { invoice_id: markingId, amount: form.amount, method: form.method, category: "deposit", reference: form.reference || null } });
      toast.success("Deposit recorded");
      setMarkingId(null);
      qc.invalidateQueries({ queryKey: ["accounting"] });
    } catch (e) { toast.error((e as Error).message); }
  };

  const depositPayments = useMemo(() => {
    const start = startOf(rangeId);
    const end = endOf(rangeId);
    return ((payments ?? []) as any[])
      .filter((p) => (p.category ?? p.kind) === "deposit" && p.status !== "void")
      .filter((p) => {
        if (!start) return true;
        const t = new Date(p.recorded_at).getTime();
        if (t < start.getTime()) return false;
        if (end && t > end.getTime()) return false;
        return true;
      });
  }, [payments, rangeId]);

  const totalCollected = depositPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const count = depositPayments.length;
  const avg = count ? totalCollected / count : 0;

  const pendingDepositInvoices = useMemo(() => {
    return ((invoices ?? []) as any[])
      .filter((i) => i.kind === "deposit" && i.status !== "void" && Number(i.balance_due || 0) > 0);
  }, [invoices]);
  const pendingTotal = pendingDepositInvoices.reduce((s, i) => s + Number(i.balance_due || 0), 0);

  const exportCsv = () => {
    const rows = depositPayments.map((p: any) => ({
      date: p.recorded_at,
      customer: p.invoices?.customer_name ?? p.customer_projects?.customer_name ?? "",
      job: p.customer_projects?.project_name ?? "",
      invoice: p.invoices?.invoice_number ?? "",
      method: p.method,
      amount: p.amount,
      reference: p.reference ?? "",
    }));
    downloadCSV(`deposits_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows));
  };

  if (loadingPay || loadingInv) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRangeId(r.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                rangeId === r.id ? "bg-white text-zinc-950 border-white" : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} disabled={!count} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200 hover:bg-white/5 disabled:opacity-40">
          <Download className="size-3.5" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat icon={<Wallet className="size-4 text-emerald-300" />} label="Deposits collected" value={fmtMoney(totalCollected)} sub={`${count} payment${count === 1 ? "" : "s"}`} tone="positive" />
        <Stat label="Average deposit" value={fmtMoney(avg)} />
        <Stat icon={<AlertCircle className="size-4 text-amber-300" />} label="Pending deposit invoices" value={fmtMoney(pendingTotal)} sub={`${pendingDepositInvoices.length} awaiting payment`} tone="accent" />
      </div>

      <section>
        <h2 className="text-sm text-zinc-300 font-medium mb-3">Collected deposits</h2>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Job</th>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Reference</th>
              </tr>
            </thead>
            <tbody>
              {count === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-500">No deposits collected in this range.</td></tr>
              ) : depositPayments.map((p: any) => (
                <tr key={p.id} className="border-b border-white/5">
                  <td className="px-4 py-3 text-zinc-300 text-xs">{fmtDate(p.recorded_at)}</td>
                  <td className="px-4 py-3 text-white">{p.invoices?.customer_name ?? p.customer_projects?.customer_name ?? "—"}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{p.customer_projects?.project_name ?? "—"}</td>
                  <td className="px-4 py-3 text-amber-300 font-mono text-xs">
                    {p.invoice_id ? (
                      <Link to="/admin/accounting/invoices/$id" params={{ id: p.invoice_id }} className="hover:underline">
                        {p.invoices?.invoice_number ?? "—"}
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-zinc-300 text-xs capitalize">{p.method?.replace?.("_", " ")}</td>
                  <td className="px-4 py-3 text-right text-emerald-300">{fmtMoney(p.amount)}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{p.reference ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-sm text-zinc-300 font-medium mb-3">Pending deposit invoices</h2>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Issued</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pendingDepositInvoices.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-500">No pending deposit invoices.</td></tr>
              ) : pendingDepositInvoices.map((i: any) => (
                <tr key={i.id} className="border-b border-white/5">
                  <td className="px-4 py-3 text-amber-300 font-mono text-xs">
                    <Link to="/admin/accounting/invoices/$id" params={{ id: i.id }} className="hover:underline">{i.invoice_number}</Link>
                  </td>
                  <td className="px-4 py-3 text-white">{i.customer_name ?? "—"}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(i.issue_date)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{i.due_date ? fmtDate(i.due_date) : "—"}</td>
                  <td className="px-4 py-3 text-zinc-300 text-xs capitalize">{i.status}</td>
                  <td className="px-4 py-3 text-right text-amber-300">{fmtMoney(i.balance_due)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openMark(i)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-200 text-[11px] hover:bg-emerald-400/20">
                      <CheckCircle2 className="size-3" /> Mark collected
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {markingId ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setMarkingId(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-serif text-xl">Record deposit</h3>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Amount</span>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Method</span>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as any })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white">
                {["cash", "check", "ach", "zelle", "venmo", "credit_card_external", "quickbooks", "other"].map((m) => (
                  <option key={m} value={m}>{m.replace("_", " ")}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Reference (check #, txn id)</span>
              <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setMarkingId(null)} className="px-3 py-1.5 rounded-full text-xs text-zinc-300 hover:bg-white/5">Cancel</button>
              <button onClick={saveMark} className="px-3 py-1.5 rounded-full text-xs bg-white text-zinc-950 font-semibold">Save deposit</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ icon, label, value, sub, tone }: { icon?: React.ReactNode; label: string; value: string; sub?: string; tone?: "positive" | "accent" }) {
  const c = tone === "positive" ? "text-emerald-300" : tone === "accent" ? "text-amber-300" : "text-white";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
      </div>
      <p className={`mt-2 font-serif text-2xl ${c}`}>{value}</p>
      {sub ? <p className="text-[11px] text-zinc-500 mt-1">{sub}</p> : null}
    </div>
  );
}